import { Dropdown, Input, Modal } from "antd";
import _, { flow } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { BiLoaderCircle, BiSearch, BiX } from "react-icons/bi";
import { FiDownload } from "react-icons/fi";
import { MdFilterList } from "react-icons/md";
import { getWhatsappFlowMessage } from "../../../../api/dmmeendpoints/getWhatsappFlows";
import SwitchComponent from "../../../../components/Switch/SwitchComponent";
import ChatView from "./ChatView/ChatView";
import "./MessageReplies.scss";
import ReplyListItem from "./ReplyListItem/ReplyListItem";
import { LuCode, LuCode2, LuUsers } from "react-icons/lu";
import { TbCodeCircle, TbUserX } from "react-icons/tb";
import chatStarted from "../../../../assets/Dashboard/chat-started.svg";
import chatCompleted from "../../../../assets/Dashboard/chat-completed.svg";
import { downloadConversationAsCSV } from "../../../../api/dmmeendpoints/downloadConversationAsCSV";
import saveAs from "file-saver";
import { trackIdsToFilterFormat } from "../../../../helpers/trackIdsToFilterFormat";
import moment from "moment";

export default function MessageReplies({
  open,
  setSeeReplies,
  isFlow = false,
  data = null,
}) {
  const {
    avatar,
    sentTo = "all",
    title,
    message,
    images,
    raffleIds,
    sent,
    eyes,
    clicks,
    replies,
    date,
    id,
    metadata,
  } = isFlow ? {} : open;

  const [activeChat, setActiveChat] = useState(0);
  const [smartView, setSmartView] = useState(true);
  const [searchVal, setSearchVal] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [activeFilter, setActiveFilter] = useState("everyone");
  const [stepFilter, setStepFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const formattedReplies = useMemo(
    () => _.groupBy(metadata?.fan_replies, "from"),
    [metadata?.fan_replies]
  );
  useEffect(() => {
    setActiveChat(Object.keys(formattedReplies)[0]);
  }, [formattedReplies]);

  const searchNumbers = (e) => {
    setSearchVal(e.target.value);
  };

  const dbUser = JSON.parse(localStorage.getItem("dbUser"));

  useEffect(() => {
    const loadContacts = async () => {
      setLoading(true);
      const response = await getWhatsappFlowMessage(open);
      setContacts(response.msg_status_query_result || []);
      setLoading(false);
      if (response.msg_status_query_result?.[0]?.contact_phone_number) {
        setActiveChat(
          response.msg_status_query_result?.[0]?.contact_phone_number
        );
      }
    };

    if (isFlow && open) {
      loadContacts();
    }
  }, [isFlow, open]);

  const dropdownItems = useMemo(() => {
    if (isFlow) {
      const options = {};
      const trackIdCounts = {};

      // Loop through contacts and build the trackIdCounts
      for (let i = 0; i < contacts.length; i++) {
        const contact = contacts[i];
        const formattedTrackId = trackIdsToFilterFormat(contact.track_id);

        if (!options[contact.track_id]) {
          options[contact.track_id] = formattedTrackId;
          trackIdCounts[contact.track_id] = 0; // Initialize count
        }

        trackIdCounts[contact.track_id] += 1; // Increment count
      }

      let items = Object.keys(options).map((key) => {
        const label = options[key];
        return {
          key: key,
          textLabel: label,
          label: (
            <div onClick={() => setActiveFilter(key)}>
              <DropdownItem
                lowOpacity={label.toLowerCase().includes("test")}
                selected={activeFilter === key}
                Icon={label.toLowerCase().includes("test") && TbCodeCircle}
                image={
                  !label.toLowerCase().includes("test") &&
                  (label.toLowerCase().includes("completed")
                    ? chatCompleted
                    : chatStarted)
                }
                label={label}
                number={trackIdCounts[key]} // Use the count instead of contacts.length
              />
            </div>
          ),
        };
      });

      items.sort((a, b) => {
        const aText = options[a.key].toLowerCase();
        const bText = options[b.key].toLowerCase();

        const isAStarted = aText.includes("started");
        const isBStarted = bText.includes("started");
        const isACompleted = aText.includes("completed");
        const isBCompleted = bText.includes("completed");
        const isAQ = aText.includes("q");
        const isBQ = bText.includes("q");
        const isATest = aText.includes("test");
        const isBTest = bText.includes("test");

        // Function to extract the first number from a string
        const extractNumber = (text) => {
          const match = text.match(/\d+/);
          return match ? parseInt(match[0], 10) : null;
        };

        const aNum = extractNumber(aText);
        const bNum = extractNumber(bText);

        // Prioritize "Started" first
        if (isAStarted && !isBStarted) return -1;
        if (!isAStarted && isBStarted) return 1;

        // Then "Completed"
        if (isACompleted && !isBCompleted) return -1;
        if (!isACompleted && isBCompleted) return 1;

        // Then "Q" items
        if (isAQ && !isBQ) return -1;
        if (!isAQ && isBQ) return 1;

        // If both have numbers, sort numerically
        if (aNum !== null && bNum !== null && aNum !== bNum) {
          return aNum - bNum;
        }

        // Sort alphabetically if no numbers or numbers are the same
        const alphabeticalOrder = aText.localeCompare(bText);
        if (!isATest && !isBTest) return alphabeticalOrder;

        // Place "Test" items at the end
        return isATest - isBTest || alphabeticalOrder;
      });

      // Prepend "Everyone" at the beginning
      items.unshift({
        key: "1",
        textLabel: "everyone",
        label: (
          <div onClick={() => setActiveFilter("everyone")}>
            <DropdownItem
              Icon={LuUsers}
              selected={activeFilter === "everyone"}
              label={"Everyone"}
              number={contacts.length || 0}
            />
          </div>
        ),
      });
      items.unshift({
        key: "0",
        disabled: true,
        label: (
          <div className="replies-filter-search">
            <Input
              prefix={<BiSearch />}
              placeholder="Search for a step"
              className="replies-filter-input"
              onChange={(e) => {
                setStepFilter(e.target.value);
              }}
            />
            <b>Filter by last step in the conversation</b>
          </div>
        ),
      });

      return items;
    }
    return [];
  }, [isFlow, contacts, activeFilter]);

  useEffect(() => {
    setStepFilter("");
  }, [activeFilter]);

  const downloadCSV = async () => {
    setLoadingDownload(true);
    try {
      const csvData = await downloadConversationAsCSV(data.flowid);
      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
      if (window.showSaveFilePicker) {
        try {
          const handle = await window.showSaveFilePicker({
            startIn: "downloads",
            suggestedName: `${
              isFlow ? dbUser?.flowNames?.[open]?.title || open : title
            }.csv`,
          });
          if (handle) {
            const writable = await handle.createWritable();
            if (writable) {
              await writable.write(blob);
              writable.close();
            }
          }
        } catch (error) {
          saveAs(
            blob,
            `${isFlow ? dbUser?.flowNames?.[open]?.title || open : title}.csv`
          );
        }
      } else {
        saveAs(
          blob,
          `${isFlow ? dbUser?.flowNames?.[open]?.title || open : title}.csv`
        );
      }
    } catch (error) {
      console.log(error, "ERROR BEFORE BLOB");
    }
    setLoadingDownload(false);
  };
  if (!open) {
    return null;
  }

  return (
    <Modal
      open={open}
      onCancel={() => {
        setSeeReplies(false);
        setSearchVal("");
        setStepFilter("");
        setActiveFilter("everyone");
      }}
      className="replies-modal"
      footer={null}
      closeIcon={
        <div className="replies-modal-close">
          <BiX size={24} color="white" />
        </div>
      }
    >
      <div className="replies-modal-body">
        <div className="replies-sidebar">
          <div className="replies-top">
            <div className="is-flow-header">
              <h3>
                {isFlow ? dbUser?.flowNames?.[open]?.title || open : title}
              </h3>
              {isFlow && (
                <div className="is-flow-actions">
                  {loadingDownload ? (
                    <BiLoaderCircle
                      size={24}
                      className="loader-circle-download"
                    />
                  ) : (
                    <FiDownload size={24} onClick={downloadCSV} role="button" />
                  )}
                  <Dropdown
                    menu={{
                      items: dropdownItems?.filter((item) => {
                        if (item.textLabel && stepFilter) {
                          return item.textLabel
                            .toLowerCase()
                            .includes(stepFilter.toLowerCase());
                        }
                        return true;
                      }),
                    }}
                    trigger={["click"]}
                    placement="bottomRight"
                    overlayClassName="replies-filter-dropdown"
                  >
                    <MdFilterList size={24} />
                  </Dropdown>
                </div>
              )}
            </div>

            {!isFlow ? (
              <b>You have {metadata?.fan_replies?.length} replies</b>
            ) : (
              <div className="smart-view-switch">
                {/* <SwitchComponent
                  data={smartView}
                  switchOnChange={(value) => setSmartView(value)}
                  labelName={`Smart View ${smartView ? "On" : "Off"}`}
                /> */}
              </div>
            )}
            <div className="replies-search">
              <Input
                prefix={<BiSearch />}
                placeholder="Search"
                className="replies-search-input"
                onChange={searchNumbers}
              />
            </div>
          </div>
          <div className="replies-number-list">
            {isFlow
              ? contacts
                  .filter((contact) => {
                    if (activeFilter && activeFilter !== "everyone") {
                      return contact.track_id === activeFilter;
                    }
                    return true;
                  })
                  .sort(
                    (a, b) =>
                      Number(moment(b.msg_received_time).format("x")) -
                      Number(moment(a.msg_received_time).format("x"))
                  )
                  .map(
                    (
                      {
                        last_msg_text,
                        contact_phone_number,
                        message_count,
                        contact_display_name,
                        msg_received_time,
                      },
                      i
                    ) =>
                      (!searchVal ||
                        contact_phone_number.includes(searchVal) ||
                        contact_display_name
                          .toLowerCase()
                          .includes(searchVal.toLowerCase())) && (
                        <ReplyListItem
                          key={contact_phone_number + i}
                          number={contact_phone_number}
                          displayName={contact_display_name}
                          message={last_msg_text}
                          isActive={activeChat === contact_phone_number}
                          noOfReplies={message_count}
                          setActiveChat={setActiveChat}
                          date={msg_received_time}
                        />
                      )
                  )
              : Object.entries(formattedReplies)?.map(
                  ([number, data], i) =>
                    (!searchVal || number.includes(searchVal)) && (
                      <ReplyListItem
                        key={i}
                        number={number}
                        message={data[data.length - 1]?.data?.Body}
                        isActive={activeChat === number}
                        noOfReplies={data.length}
                        setActiveChat={setActiveChat}
                      />
                    )
                )}
          </div>
        </div>

        <div className="chat-view">
          <ChatView
            messages={formattedReplies[activeChat]}
            artistBroadcast={message}
            images={images}
            raffleIds={raffleIds}
            broadcastDate={date}
            id={id || open}
            phoneNumber={activeChat}
            setActiveChat={setActiveChat}
            formattedReplies={formattedReplies}
            isFlow={isFlow}
            contact={
              contacts[
                contacts.findIndex(
                  (contact) => contact.contact_phone_number === activeChat
                )
              ]
            }
            contacts={isFlow ? contacts : []}
            loading={loading && isFlow}
          />
        </div>
      </div>
    </Modal>
  );
}

const DropdownItem = ({
  Icon,
  label,
  number,
  image = null,
  lowOpacity,
  selected,
}) => {
  return (
    <div
      className={`message-replies-dropdown-item ${
        lowOpacity ? "message-replies-dropdown-item-low-opacity" : ""
      }
      ${selected ? "message-replies-dropdown-item-selected" : ""}
      `}
    >
      <div>
        {image ? (
          <img src={image} width={18} height={18} alt={label} />
        ) : (
          <Icon size={18} color={lowOpacity && "#A0A0AB"} />
        )}
        <span>{label}</span>
      </div>
      <div className="message-replies-dropdown-badge">{number}</div>
    </div>
  );
};
